import { useState, useEffect } from "react";
import { getAuditTrail, AuditEvent } from "../api";

const ACTION_CONFIG: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  created: { color: "#10b981", bg: "rgba(16,185,129,0.08)", icon: "+", label: "Created" },
  updated: { color: "#6366f1", bg: "rgba(99,102,241,0.08)", icon: "~", label: "Updated" },
  status_changed: { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", icon: "->", label: "Status" },
  deleted: { color: "#ef4444", bg: "rgba(239,68,68,0.08)", icon: "x", label: "Deleted" },
  resolved: { color: "#0891b2", bg: "rgba(8,145,178,0.08)", icon: "=", label: "Resolved" },
  simulated: { color: "#8b5cf6", bg: "rgba(139,92,246,0.08)", icon: "?", label: "What-If" },
};

const ENTITY_ICONS: Record<string, string> = {
  feature: "x",
  dataset: "D",
  variable: "V",
  interview: "I",
};

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function AuditTrailPanel() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState<string | null>(null);
  const [filterEntity, setFilterEntity] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getAuditTrail(undefined, undefined, 200)
      .then(res => setEvents(res.data))
      .catch(() => setEvents(DEMO_EVENTS))
      .finally(() => setLoading(false));
  }, []);

  const filtered = events.filter(e => {
    if (filterAction && e.action !== filterAction) return false;
    if (filterEntity && e.entity_type !== filterEntity) return false;
    return true;
  });

  const actionCounts: Record<string, number> = {};
  events.forEach(e => { actionCounts[e.action] = (actionCounts[e.action] || 0) + 1; });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Audit Trail
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(99,102,241,0.1)", color: "#6366f1" }}>
            {events.length} events
          </span>
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Every extraction, update, and status change — traced back to source
        </p>
      </div>

      {/* Filters */}
      <div style={{ padding: "12px 32px", flexShrink: 0, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {Object.entries(ACTION_CONFIG).map(([action, cfg]) => {
          const count = actionCounts[action] || 0;
          if (count === 0 && action !== "created") return null;
          const isActive = filterAction === action;
          return (
            <button key={action} onClick={() => setFilterAction(isActive ? null : action)} style={{
              padding: "4px 12px", borderRadius: 20, border: `1.5px solid ${isActive ? cfg.color + "60" : "rgba(0,0,0,0.06)"}`,
              background: isActive ? cfg.bg : "transparent", cursor: "pointer",
              fontSize: 10, fontWeight: 600, color: isActive ? cfg.color : "rgba(0,0,0,0.5)",
              display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s ease",
            }}>
              {cfg.label}
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 800 }}>{count}</span>
            </button>
          );
        })}
        <div style={{ width: 1, background: "rgba(0,0,0,0.08)", margin: "0 4px" }} />
        {["feature", "dataset", "interview"].map(entity => {
          const isActive = filterEntity === entity;
          return (
            <button key={entity} onClick={() => setFilterEntity(isActive ? null : entity)} style={{
              padding: "4px 10px", borderRadius: 20, border: `1.5px solid ${isActive ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.06)"}`,
              background: isActive ? "rgba(0,0,0,0.04)" : "transparent", cursor: "pointer",
              fontSize: 10, fontWeight: 600, color: isActive ? "#000" : "rgba(0,0,0,0.4)",
              textTransform: "capitalize", transition: "all 0.15s ease",
            }}>
              {entity}s
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div style={{ flex: 1, overflow: "auto", padding: "0 32px 40px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "rgba(0,0,0,0.3)", fontSize: 12 }}>Loading audit trail...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "rgba(0,0,0,0.3)", fontSize: 12 }}>
            No events{filterAction || filterEntity ? " matching filters" : " yet"} — start extracting variables to generate audit events
          </div>
        ) : (
          <div style={{ position: "relative", paddingLeft: 24 }}>
            {/* Timeline line */}
            <div style={{ position: "absolute", left: 7, top: 8, bottom: 0, width: 2, background: "rgba(0,0,0,0.06)" }} />

            {filtered.map((event, idx) => {
              const cfg = ACTION_CONFIG[event.action] || ACTION_CONFIG.created;
              const isExpanded = expandedId === event.id;
              return (
                <div key={event.id || idx} style={{ position: "relative", marginBottom: 4 }}>
                  {/* Dot */}
                  <div style={{
                    position: "absolute", left: -20, top: 12,
                    width: 16, height: 16, borderRadius: "50%",
                    background: cfg.bg, border: `2px solid ${cfg.color}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, fontWeight: 900, color: cfg.color, fontFamily: "'IBM Plex Mono', monospace",
                  }}>
                    {ENTITY_ICONS[event.entity_type] || "?"}
                  </div>

                  {/* Card */}
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : event.id)}
                    style={{
                      padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                      border: `1px solid ${isExpanded ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.04)"}`,
                      background: isExpanded ? "rgba(0,0,0,0.01)" : "transparent",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        fontSize: 8, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                        background: cfg.bg, color: cfg.color,
                      }}>{cfg.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
                        {event.entity_name || event.entity_id}
                      </span>
                      <span style={{ fontSize: 9, color: "rgba(0,0,0,0.3)", fontFamily: "'IBM Plex Mono', monospace" }}>
                        {timeAgo(event.timestamp)}
                      </span>
                    </div>

                    <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", marginTop: 3 }}>
                      <span style={{ textTransform: "capitalize" }}>{event.entity_type}</span>
                      {event.actor !== "system" && <> by <strong>{event.actor}</strong></>}
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                        {/* Source citation */}
                        {event.source_quote && (
                          <div style={{
                            padding: "10px 14px", borderRadius: 8, marginBottom: 8,
                            background: "rgba(99,102,241,0.04)", borderLeft: "3px solid rgba(99,102,241,0.3)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(99,102,241,0.6)", marginBottom: 4 }}>
                              Source Citation
                            </div>
                            <div style={{ fontSize: 11, fontStyle: "italic", color: "rgba(0,0,0,0.6)", lineHeight: 1.6 }}>
                              "{event.source_quote}"
                            </div>
                          </div>
                        )}

                        {/* Details */}
                        {Object.keys(event.details).length > 0 && (
                          <div style={{
                            padding: "10px 14px", borderRadius: 8,
                            background: "rgba(0,0,0,0.02)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 6 }}>
                              Details
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                              {Object.entries(event.details).map(([key, val]) => (
                                <div key={key} style={{ fontSize: 10 }}>
                                  <span style={{ color: "rgba(0,0,0,0.35)" }}>{key}: </span>
                                  <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>
                                    {typeof val === "number" ? (val as number).toFixed(4) : String(val)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div style={{ fontSize: 9, color: "rgba(0,0,0,0.25)", marginTop: 8, fontFamily: "'IBM Plex Mono', monospace" }}>
                          {event.id} | {event.timestamp}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* Demo events for when backend has no data yet */
const DEMO_EVENTS: AuditEvent[] = [
  { id: "evt_demo_001", timestamp: new Date(Date.now() - 120000).toISOString(), action: "created", entity_type: "feature", entity_id: "feat_abc123", entity_name: "Social_Isolation_Score", actor: "nlp_engine", details: { certainty: 0.72 }, source_quote: "Children who are socially isolated tend to have worse outcomes, I'd say about 60-70% of the time..." },
  { id: "evt_demo_002", timestamp: new Date(Date.now() - 90000).toISOString(), action: "created", entity_type: "feature", entity_id: "feat_def456", entity_name: "Parental_Conflict_Level", actor: "nlp_engine", details: { certainty: 0.85 }, source_quote: "High-conflict divorce is the single biggest predictor — it's almost always damaging..." },
  { id: "evt_demo_003", timestamp: new Date(Date.now() - 60000).toISOString(), action: "status_changed", entity_type: "feature", entity_id: "feat_abc123", entity_name: "Social_Isolation_Score", actor: "analyst", details: { from: "candidate", to: "pending" }, source_quote: "" },
  { id: "evt_demo_004", timestamp: new Date(Date.now() - 45000).toISOString(), action: "updated", entity_type: "feature", entity_id: "feat_abc123", entity_name: "Social_Isolation_Score", actor: "analyst", details: { certainty: 0.78, prior_type: "Normal" }, source_quote: "" },
  { id: "evt_demo_005", timestamp: new Date(Date.now() - 30000).toISOString(), action: "simulated", entity_type: "feature", entity_id: "feat_abc123", entity_name: "Social_Isolation_Score", actor: "analyst", details: { perturbation_sigma: 2.0, direction: "up" }, source_quote: "" },
  { id: "evt_demo_006", timestamp: new Date(Date.now() - 15000).toISOString(), action: "status_changed", entity_type: "feature", entity_id: "feat_abc123", entity_name: "Social_Isolation_Score", actor: "analyst", details: { from: "pending", to: "approved" }, source_quote: "" },
  { id: "evt_demo_007", timestamp: new Date(Date.now() - 5000).toISOString(), action: "created", entity_type: "dataset", entity_id: "ds_demo_001", entity_name: "Demo: Custody Outcome Prediction", actor: "system", details: { n_rows: 1000, n_cols: 6 }, source_quote: "" },
];
